// Core imports
import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';

// Component Imports
import { Button, Col, Dropdown, Menu, Radio, Row, Space, Typography } from 'antd';

// Misc imports
import { t } from 'i18next';
import ExportTranslationsTable from '../Tables/ExportTranslationsTable';
import { EXPORT_RADIO_OPTIONS, EXPORT_TABLE_ENTITIES, TRANSLATION_FILE_TYPES } from '../../constants/translations';
import { onExportTranslations } from '../../utils/misc/handleExportContentTranslation';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useParams } from 'react-router-dom';

const ExportTranslationsForm = ({
  onExportComplete = () => {},
  onCancel = () => {}
}) => {
  const { orgId, tenantId } = useParams();
  const { primaryBotLanguage, botLanguages } = useBotLanguage();
  const { data: bot } = getCurrentQueryState('fetchBot', { orgId, tenantId });

  const [selectedOption, setSelectedOption] = useState(EXPORT_RADIO_OPTIONS.SELECT_ALL.key);
  const [selectedData, setSelectedData] = useState({});

  const updateSelectedData = (data) => {
    setSelectedData(data);
  };

  const handleOptionChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const onExport = (fileType) => {
    try {
      onExportTranslations({
        fileType,
        options: {
          exportData: selectedData,
          primaryExportLanguage: primaryBotLanguage,
          timezone: bot?.timezone,
          fileName: bot?.displayName,
          exportLanguages: botLanguages.filter(language => language !== primaryBotLanguage)
        }
      });
    } catch(e) {
      console.error(e);
    }

    onExportComplete();
  };

  const renderSelectedEntitiesFooterDisplay = () => {
    const dataTotalStringArray = Object.entries(selectedData).map(([key, value]) => {
      return ` ${value?.length} ${Object.values(EXPORT_TABLE_ENTITIES).find(item => item.key === key)?.tabLabel}`;
    });

    return (
      <Fragment>
        <Typography.Text>
          {selectedOption === EXPORT_RADIO_OPTIONS.SELECT_ALL.key ? t('All selected') : t('Selected')}:
        </Typography.Text>

        <Typography.Text className="fw-600">
          {dataTotalStringArray?.join(',')}
        </Typography.Text>
      </Fragment>
    );
  };

  const renderRadioGroup = () => {
    return (
      <Radio.Group className="ml-4" onChange={handleOptionChange} value={selectedOption} defaultValue={EXPORT_RADIO_OPTIONS.SELECT_ALL.key}>
        <Space direction="vertical">
          {
            Object.values(EXPORT_RADIO_OPTIONS).map(radioOption => (
              <Radio key={radioOption.key} value={radioOption.key}>{radioOption.label}</Radio>
            ))
          }
        </Space>
      </Radio.Group>
    );
  };

  const menu = () => (
    <Menu
      onClick={(item) => onExport(item.key)}
      items={
        Object.values(TRANSLATION_FILE_TYPES)
          .filter(fileType => fileType.allowedExport)
          .map(fileType => (
            {
              label: fileType.label,
              key: fileType.key
            }
          ))
      }
    />
  );

  return (
    <Fragment>
      <div className="px-6 pt-6 flex-grow-1">
        <Row>
          <Col xs={24}>
            <Space direction="vertical" className="w-100" size="middle">
              <Typography.Text className="fs-lg fw-500">{t('Select agent content for translation:')}</Typography.Text>
              {renderRadioGroup()}
            </Space>
          </Col>
          <Col xs={24}>
            <Row>
              <Col xs={24}>
                <ExportTranslationsTable
                  selectedRadioOption={selectedOption}
                  updateSelectedData={updateSelectedData}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      <div className="ant-drawer-footer">
        <Row align="middle" justify="space-between">
          <Col>
            {renderSelectedEntitiesFooterDisplay()}
          </Col>

          <Col>
            <Space>
              <Button
                size="large"
                onClick={onCancel}
              >
                {t('Cancel')}
              </Button>

              <Dropdown overlay={menu}
                disabled={!Object.values(selectedData).some(entityArray => entityArray.length > 0)}
                trigger={['click']}
              >
                <Button
                  type="primary"
                  size="large"
                  disabled={!Object.values(selectedData).some(entityArray => entityArray.length > 0)}
                >
                  {selectedOption === EXPORT_RADIO_OPTIONS.SELECT_ALL.key ? t('Export All Content') : t('Export Selected')}
                </Button>
              </Dropdown>
            </Space>
          </Col>
        </Row>
      </div>
    </Fragment>

  );
};

ExportTranslationsForm.propTypes = {
  onExportComplete: PropTypes.func,
  onCancel: PropTypes.func
};

export default ExportTranslationsForm;
